/**
 * What a car is and what it offers, in the reader's language (#369).
 *
 * A class or a feature travels as a code. The starter ones — `standard`, `estate`, `van`;
 * `suitcases`, `small_pet`, `big_pet`, `card_payment`, `baby_stroller`, `wheelchair` — have
 * words in the shared bundle
 * (`vehicles.class.*`, `vehicles.feature.*`), so the console, the driver app and the
 * passenger app say them alike. One a firm added itself has none, and is shown by the name
 * the firm gave it; a code nobody named is shown as it is.
 *
 * A starter the firm renamed (#462) is the firm's words, not ours: `namedByFirm` says so, and
 * then its `displayName` wins over the translation. A screen holding only an order's codes
 * asks `useFeatureName` in `featureNames.ts`, which has the list.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function className(t: Translate, code: string, displayName?: string | null): string {
  return t(`vehicles.class.${code}`, { defaultValue: displayName ?? code });
}

export function featureName(
  t: Translate,
  code: string,
  displayName?: string | null,
  namedByFirm = false,
): string {
  if (namedByFirm && displayName) return displayName;

  return t(`vehicles.feature.${code}`, { defaultValue: displayName ?? code });
}

/**
 * The features a form offers: the active ones, and any switched-off one already chosen.
 *
 * The lists carry every label, switched-off ones included, so that a car or an old order
 * holding one can still be named (#462). A form offers only what is in use — but a car that
 * already has a switched-off label must still show it, or the office could never untick it,
 * and an order copied from one must not lose it without anybody seeing.
 */
export function offeredFeatures<T extends { code: string; isActive: boolean }>(
  features: readonly T[],
  chosen: readonly string[],
): T[] {
  return features.filter((feature) => feature.isActive || chosen.includes(feature.code));
}

/**
 * The starter features in the order a new firm's order form draws them — the pets, then
 * suitcases and the stroller, then card and wheelchair, two to a row of its narrow column — the
 * same as `VehicleFeature.Defaults()` on the server; a firm's own come after.
 */
const STARTER_ORDER = ['small_pet', 'big_pet', 'suitcases', 'baby_stroller', 'card_payment', 'wheelchair'];

/** Feature codes in the order a person reads them on the form, for a screen with no list of its own. */
export function inFormOrder(codes: readonly string[]): string[] {
  const at = (code: string) => {
    const index = STARTER_ORDER.indexOf(code);
    return index === -1 ? STARTER_ORDER.length : index;
  };

  return [...codes].sort((a, b) => at(a) - at(b) || a.localeCompare(b));
}

/** One word, where a row has room for little more: the order form. A firm's own uses its name. */
export function featureShortName(
  t: Translate,
  code: string,
  displayName?: string | null,
  namedByFirm = false,
): string {
  if (namedByFirm && displayName) return displayName;

  return t(`vehicles.featureShort.${code}`, { defaultValue: featureName(t, code, displayName) });
}
